import { Injectable, OnDestroy, QueryList } from '@angular/core';
import { Reservation, Shift, Table, TableSlot } from '@model/reservation';
import { ConnectionEdge, ReservationType, TableConnection } from '@model/tables-plan';
import { Store, select } from '@ngrx/store';
import { State } from '@store/index';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';
import { ScrollDistance } from './canvas.component';
import { TableComponent } from './table/table.component';

export type SlotNumber = 1 | 2 | 3;

export type TableJoin = {
  table1: Table,
  slot1: SlotNumber,
  table2: Table,
  slot2: SlotNumber,
}

@Injectable({
  providedIn: 'root'
})
export class TableConnectionsService implements OnDestroy {

  constructor(
    private reservationService: ReservationService,
    private store: Store<State>,
  ) {
    this.selectedShift$.pipe(takeUntil(this.destroy$)).subscribe((selectedShift: Shift | null | undefined) => {
      this.selectedShift = selectedShift;
    });
  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedShift: Shift | null | undefined = null;

  getConnections(
    tables: Table[],
    tablesRef: QueryList<TableComponent>,
    canvasRect: DOMRect | null,
    canvasScrollDistance: ScrollDistance,
    zoom: number,
    reservations: Reservation[]
  ) {

    const lines: TableConnection[] = [];

    if (tablesRef == null) {
      return lines;
    }

    const tableJoins = this.getTableJoins(reservations, tables);

    for (let tableJoin of tableJoins) {
      const tableRef1 = tablesRef.find(t => t.table?.tableId != null && t.table.tableId === tableJoin.table1.tableId);
      const tableRef2 = tablesRef.find(t => t.table?.tableId != null && t.table.tableId === tableJoin.table2.tableId);

      if (tableRef1 == null || tableRef2 == null) {
        continue;
      }

      const line = this.getConnectionLine(tableRef1, tableRef2, canvasRect, canvasScrollDistance, zoom, tableJoin.slot1, tableJoin.slot2);

      if (line == null) {
        continue;
      }

      lines.push(line);
    }

    return lines;
  }

  relativeRect(rect: DOMRect | null, canvasRect: DOMRect | null, canvasScrollDistance: ScrollDistance): DOMRect | null {
    if (rect == null || canvasRect == null) {
      return rect;
    }

    return {
      ...rect,
      bottom: rect.bottom,
      height: rect.height,
      left: rect.left - canvasRect.left + canvasScrollDistance.scrollLeft,
      right: rect.right,
      top: rect.top - canvasRect.top + canvasScrollDistance.scrollTop,
      width: rect.width,
      x: rect.x - canvasRect.x + canvasScrollDistance.scrollLeft,
      y: rect.y - canvasRect.y + canvasScrollDistance.scrollTop,
    };
  }

  private getTableJoins(reservations: Reservation[], tables: Table[]) {

    const tableJoins: TableJoin[] = [];

    // dictionary: Reservations by Table Id
    const reservationsByTableDictionary: { [id: string]: Reservation[]; } = {};
    for (let table of tables) {
      const tableReservations = reservations
        .filter(r => Common.tableReservationsFilter(table, r))
        .filter(r => Common.completedReservationsFilter(r))
        .sort(Common.arrivalTimeComparator);

      reservationsByTableDictionary[table.tableId] = tableReservations;
    }

    for (let reservation of reservations) {

      if (!this.isMultiTableReservation(reservation)) {
        continue;
      }

      const tableNames = Common.getResrvationAllocatedTableNames(reservation);

      const reservationTables = tables
        .filter(t => tableNames.includes(t.tableName))
        .sort(Common.tableOrderComperator);

      if (reservationTables.length <= 1) {
        continue;
      }

      for (let i = 0; i < reservationTables.length - 1; i++) {
        const table1 = reservationTables[i];
        const table2 = reservationTables[i + 1];

        const table1Reservations = reservationsByTableDictionary[table1.tableId];
        const table2Reservations = reservationsByTableDictionary[table2.tableId];

        if (table1Reservations == null || table2Reservations == null) {
          continue;
        }

        const table1Slots = this.reservationService.getTableSlots(table1Reservations, this.selectedShift);
        const table2Slots = this.reservationService.getTableSlots(table2Reservations, this.selectedShift);

        const slot1: SlotNumber | null = this.getSlotNumber(table1Slots, reservation);
        const slot2: SlotNumber | null = this.getSlotNumber(table2Slots, reservation);

        if (slot1 == null || slot2 == null) {
          continue;
        }

        // const slot1 = table1Reservations
        //   .findIndex(r => r.reservationDetails.reservationId === reservation.reservationDetails.reservationId) + 1 as SlotNumber;
        // const slot2 = table2Reservations
        //   .findIndex(r => r.reservationDetails.reservationId === reservation.reservationDetails.reservationId) + 1 as SlotNumber;

        tableJoins.push({
          table1,
          slot1,
          table2,
          slot2,
        });
      }

    }

    return tableJoins;
  }

  private getSlotNumber(slots: { slot1: TableSlot, slot2: TableSlot, slot3: TableSlot }, reservation: Reservation): SlotNumber | null {
    if (slots.slot1.reservation != null
      && slots.slot1.reservation?.reservationDetails.reservationId === reservation.reservationDetails.reservationId) {
      return 1;
    }

    if (slots.slot2.reservation != null
      && slots.slot2.reservation?.reservationDetails.reservationId === reservation.reservationDetails.reservationId) {
      return 2;
    }

    if (slots.slot3.reservation != null
      && slots.slot3.reservation?.reservationDetails.reservationId === reservation.reservationDetails.reservationId) {
      return 3;
    }

    return null;
  }

  private isMultiTableReservation(reservation: Reservation): boolean {

    if (reservation?.reservationDetails?.reservationsStatus == null) {
      return false;
    }

    const type = this.reservationService.statusToType(reservation.reservationDetails.reservationsStatus);

    if (type == null || type == ReservationType.Completed) {
      return false;
    }

    const tableNames = Common.getResrvationAllocatedTableNames(reservation);

    return tableNames.length > 1;
  }

  private getConnectionLine(
    tableRef1: TableComponent,
    tableRef2: TableComponent,
    canvasRect: DOMRect | null,
    canvasScrollDistance: ScrollDistance,
    zoom: number,
    slot1Number: 1 | 2 | 3,
    slot2Number: 1 | 2 | 3,
  ): TableConnection | null {

    // console.log(canvasRect);
    // console.log(tableRef1.table?.tableName);
    // console.log(tableRef2.table?.tableName);

    if (tableRef1 == null || tableRef2 == null) {
      return null;
    }

    let table1SlotRect;
    let table2SlotRect;
    switch (slot1Number) {
      case 1:
        table1SlotRect = tableRef1?.timeslot1Rect;
        break;
      case 2:
        table1SlotRect = tableRef1?.timeslot2Rect;
        break;
      case 3:
        table1SlotRect = tableRef1?.timeslot3Rect;
        break;
    }
    switch (slot2Number) {
      case 1:
        table2SlotRect = tableRef2?.timeslot1Rect;
        break;
      case 2:
        table2SlotRect = tableRef2?.timeslot2Rect;
        break;
      case 3:
        table2SlotRect = tableRef2?.timeslot3Rect;
        break;
    }

    const table1Slot1Rect = this.relativeRect(table1SlotRect ?? null, canvasRect, canvasScrollDistance);
    const table2Slot1Rect = this.relativeRect(table2SlotRect ?? null, canvasRect, canvasScrollDistance);

    // console.log(table1Slot1Rect);
    // console.log(table2Slot1Rect);

    const edges = this.calculateConnectionEdges(table1Slot1Rect?.x!, table1Slot1Rect?.y!, table2Slot1Rect?.x!, table2Slot1Rect?.y!);

    // console.log('edges: ', edges);

    const connectionPoint1 = this.calculateConnectionPoint(table1Slot1Rect!, edges.edge1);
    const connectionPoint2 = this.calculateConnectionPoint(table2Slot1Rect!, edges.edge2);

    // console.log(connectionPoint1);
    // console.log(connectionPoint2);

    return {
      x1: connectionPoint1.x / zoom,
      y1: connectionPoint1.y / zoom,
      x2: connectionPoint2.x / zoom,
      y2: connectionPoint2.y / zoom,
    };
  }

  private calculateConnectionEdges(x1: number, y1: number, x2: number, y2: number): { edge1: ConnectionEdge, edge2: ConnectionEdge } {

    let edge1: ConnectionEdge = 'Top';
    let edge2: ConnectionEdge = 'Top';

    const x = Math.abs(x2 - x1);
    const y = Math.abs(y2 - y1);

    if (x > y) {
      // left - right
      if (x2 > x1) {
        edge1 = 'Right';
        edge2 = 'Left';
      } else {
        edge1 = 'Left';
        edge2 = 'Right';
      }
    } else {
      // top - bottom
      if (y2 > y1) {
        edge1 = 'Bottom';
        edge2 = 'Top';
      } else {
        edge1 = 'Top';
        edge2 = 'Bottom';
      }
    }

    return { edge1, edge2 };
  }

  private calculateConnectionPoint(rect: DOMRect, edge: ConnectionEdge): { x: number, y: number } {
    switch (edge) {
      case 'Top':
        return { x: rect.x + rect.width / 2, y: rect.y };
      case 'Right':
        return { x: rect.x + rect.width, y: rect.y + rect.height / 2 };
      case 'Bottom':
        return { x: rect.x + rect.width / 2, y: rect.y + rect.height };
      case 'Left':
        return { x: rect.x, y: rect.y + rect.height / 2 };
      default:
        return { x: rect.x, y: rect.y };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
